import React from 'react';
import { Clock, Sunrise, Sunset, X, ArrowDown, ArrowUp, MapPin } from 'lucide-react';

/**
 * Компонент отображения списка выбранных городов с текущим временем
 */
const WorldClockDisplay = ({ selectedCities, userTimeZone, currentTime, onRemoveCity }) => {
  if (!selectedCities || selectedCities.length === 0) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-4 mb-6">
        <p className="text-amber-800 dark:text-amber-300 flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          Добавьте города в список, чтобы отслеживать их время
        </p>
      </div>
    );
  }

  // Расчет смещения часового пояса относительно UTC
  const getTimezoneOffset = (timezone) => {
    const now = new Date();
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const tzOffset = (tzDate - now) / 60000; // в минутах
    const hours = Math.floor(Math.abs(tzOffset) / 60);
    const minutes = Math.abs(tzOffset) % 60;
    
    return {
      offset: tzOffset >= 0 ? `+${hours}` : `-${hours}`,
      offsetFormatted: `UTC${tzOffset >= 0 ? '+' : '-'}${hours}${minutes > 0 ? `:${minutes}` : ''}`,
      hours,
      minutes
    };
  };

  // Расчет разницы во времени между городами и пользователем
  const getTimeDifference = (timezone) => {
    const userOffset = getTimezoneOffset(userTimeZone).offset;
    const cityOffset = getTimezoneOffset(timezone).offset;
    let difference = parseInt(cityOffset) - parseInt(userOffset);
    
    if (difference === 0) return "то же время";
    const sign = difference > 0 ? "+" : "";
    return `${sign}${difference} ч`;
  };

  // Определение дня/ночи в городе
  const isDayTime = (timezone) => {
    const now = new Date();
    const tzTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const hours = tzTime.getHours();
    return hours >= 6 && hours < 18;
  };

  // Форматирование времени для конкретного часового пояса
  const formatTime = (timezone) => {
    return currentTime.toLocaleString('ru-RU', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Форматирование даты для конкретного часового пояса
  const formatDate = (timezone) => {
    return currentTime.toLocaleString('ru-RU', {
      timeZone: timezone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Определение времени рассвета и заката (примерные фиксированные значения для демонстрации)
  const getSunriseSunset = (timezone) => {
    const isDay = isDayTime(timezone);
    // Для демонстрации используем фиксированные значения
    return {
      sunrise: '06:30',
      sunset: '20:30',
      current: isDay ? 'день' : 'ночь'
    };
  };

  return (
    <div className="mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold w-1/4">Город</th>
                <th scope="col" className="px-4 py-3 font-semibold w-1/5">Часовой пояс</th>
                <th scope="col" className="px-4 py-3 font-semibold">Текущее время</th>
                <th scope="col" className="px-4 py-3 font-semibold text-center">Разница</th>
                <th scope="col" className="px-4 py-3 font-semibold text-center">День/Ночь</th>
                <th scope="col" className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {selectedCities.map((city, index) => {
                const isDay = isDayTime(city.timezone);
                const sunTimes = getSunriseSunset(city.timezone);
                const tzOffset = getTimezoneOffset(city.timezone);
                const timeDiff = getTimeDifference(city.timezone);
                const rowBgClass = index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50';
                
                return (
                  <tr key={city.name} className={`${rowBgClass} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center">
                        <span className="mr-2">{city.flag}</span>
                        <div>
                          <div className="text-gray-900 dark:text-white font-medium">{city.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{city.country}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 dark:text-white">{tzOffset.offsetFormatted}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{city.timezone.replace('_', ' ')}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{formatTime(city.timezone)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(city.timezone)}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {timeDiff !== "то же время" ? (
                        <div className="flex flex-col items-center">
                          {parseInt(timeDiff) > 0 ? (
                            <ArrowUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <ArrowDown className="w-4 h-4 text-red-500" />
                          )}
                          <span className="text-sm font-medium">{timeDiff}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center">
                        {isDay ? (
                          <Sunrise className="w-5 h-5 text-amber-500" />
                        ) : (
                          <Sunset className="w-5 h-5 text-indigo-500" />
                        )}
                        <div className="text-xs mt-1 flex flex-col items-center">
                          <span>{isDay ? 'День' : 'Ночь'}</span>
                          <span className="text-gray-500 dark:text-gray-400">
                            {isDay ? `Закат: ${sunTimes.sunset}` : `Рассвет: ${sunTimes.sunrise}`}
                          </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => onRemoveCity(city.name)}
                          className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="mt-4 flex flex-wrap gap-2">
          {selectedCities.map(city => (
            <div 
              key={city.name}
              className="inline-flex items-center px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 text-sm"
            >
              <MapPin className="w-3.5 h-3.5 text-indigo-500 mr-1" />
              <span className="mr-1">{city.name}</span>
              <span className="text-gray-500 dark:text-gray-400 text-xs">
                ({formatTime(city.timezone)})
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  export default WorldClockDisplay;